import React, { useEffect, useRef, useState } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import classes from './PlacesAutocompleteInput.module.css';
import formClasses from './Forms.module.css';

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// google.maps.places.AutocompleteService (what react-google-places-autocomplete
// wraps) was deprecated and is no longer granted to new Google Cloud projects
// as of March 2025 — this talks to its replacement, AutocompleteSuggestion,
// directly via the new Places API.
//
// setOptions() must run exactly once before the first importLibrary() call,
// and importLibrary() itself only actually loads the script on its first
// invocation — caching the promise at module scope keeps that true across
// every mount of this component, and across the two places (create + edit)
// this component can appear.
let optionsSet = false;
let placesLibraryPromise = null;
const getPlacesLibrary = () => {
  if (!optionsSet) {
    setOptions({ key: GOOGLE_PLACES_API_KEY, v: 'weekly' });
    optionsSet = true;
  }
  if (!placesLibraryPromise) {
    placesLibraryPromise = importLibrary('places');
  }
  return placesLibraryPromise;
};

const PlacesAutocompleteInput = ({ value, onSelect, placeholder }) => {
  const [inputValue, setInputValue] = useState(value?.label || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const placesLibRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Keep the input in sync if the parent's committed value changes from
  // outside (e.g. switching from create to edit with an existing festival).
  useEffect(() => {
    setInputValue(value?.label || '');
  }, [value]);

  useEffect(() => {
    getPlacesLibrary()
      .then(lib => { placesLibRef.current = lib; })
      .catch(err => console.log('Error loading Google Maps places library:', err));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const fetchSuggestions = async (text) => {
    const lib = placesLibRef.current;
    if (!lib || !text) {
      setSuggestions([]);
      return;
    }
    // One session token covers the whole search-to-selection flow, per
    // Google's billing guidance — started on first keystroke, discarded
    // once a place is actually selected below.
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
    }
    try {
      const { suggestions: results } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: text,
        includedRegionCodes: ['au'],
        includedPrimaryTypes: ['locality', 'sublocality', 'administrative_area_level_1'],
        sessionToken: sessionTokenRef.current,
      });
      setSuggestions(results || []);
      setShowSuggestions(true);
    } catch (err) {
      console.log('Error fetching place suggestions:', err);
      setSuggestions([]);
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    setInputValue(text);
    // Typing invalidates whatever was previously selected — the field
    // requires an actual chosen suggestion (with resolvable coordinates),
    // not free text, so clear the parent's committed value until they pick
    // one again.
    onSelect(null);
    clearTimeout(debounceRef.current);
    if (!text.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 250);
  };

  const handleSelect = async (suggestion) => {
    const prediction = suggestion.placePrediction;
    const label = prediction.text.text;
    setInputValue(label);
    setShowSuggestions(false);
    setSuggestions([]);

    let lat = null;
    let lng = null;
    try {
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ['location'] });
      if (place.location) {
        lat = place.location.lat();
        lng = place.location.lng();
      }
    } catch (err) {
      console.log('Error fetching place details:', err);
    }

    // Selecting a place ends this autocomplete session — start a fresh
    // token next time the user searches again.
    sessionTokenRef.current = null;

    onSelect({ label, lat, lng });
  };

  return (
    <div className={classes.wrapper} ref={containerRef}>
      <input
        className={formClasses.formInput}
        type="text"
        required
        placeholder={placeholder}
        value={inputValue}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
        autoComplete="off"
        spellCheck="false"
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className={classes.suggestionsList}>
          {suggestions.map((s, i) => (
            <li
              key={s.placePrediction?.placeId || i}
              className={classes.suggestionItem}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
            >
              {s.placePrediction?.text?.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PlacesAutocompleteInput;
